//@ts-nocheck
import React, { useState } from "react";
import TextLabel from "../TextLabel";
import StyledButtons from "../../styledComponents/styledButtons";
import { CONSTANTS } from "../../CONSTANTS";
import { Box, Button, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { compressImage } from "../helper";
import { useGlobalStateContext } from "../../context/globalState";

interface ImageUploadProps {
  photo?: string; // Optional photo prop
  onImageChange?: (base64Image: string, fileType: string) => void; // Callback to send the image as Base64 to the parent
  setAlertBoxState: (state: { type: string; message: string }) => void; // Callback to set alert box state in parent
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  photo,
  onImageChange,
  setAlertBoxState,
  previewOnly = false,
}) => {
  const { t } = useTranslation();
  const { beginLoading, endLoading } = useGlobalStateContext();
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // Check if the file exists and if its size is less than 2MB
    if (file) {
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        setAlertBoxState({
          type: "error",
          message: t("error_messages.invalidImageType"),
        });
        return;
      }

      let loadingToken: string | null = null;
      const stopLoading = () => {
        console.log("Setting loading state to false");
        if (loadingToken) endLoading(loadingToken);
      };
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      let compressedFile = file;
      if (file.size > maxSize) {
        // compress the image if it exceeds the size limit
        loadingToken = beginLoading("image-upload-compress");
        try {
          compressedFile = await compressImage(file);
        } catch (error) {
          console.error("Error compressing image:", error);
          setAlertBoxState({
            type: "error",
            message: t("error_messages.imageCompressionError"),
          });
          stopLoading();
          return;
        }
        if (compressedFile.size > maxSize) {
          setAlertBoxState({
            type: "error",
            message: t("error_messages.imageTooLarge"),
          });
          stopLoading();
          return;
        } else {
          setAlertBoxState({ type: "", message: "" }); // Reset the alert if the compressed file size is valid
        }
      } else {
        setAlertBoxState({ type: "", message: "" }); // Reset the alert if the file size is valid
      }
      const reader = new FileReader();
      reader.onerror = stopLoading;
      reader.onloadend = () => {
        const img = new Image();
        img.onerror = stopLoading;
        img.onload = () => {
          // Create a canvas to resize the image
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Resize the image to a max of 200x200
          const maxSize = 200;
          const ratio = Math.min(maxSize / img.width, maxSize / img.height);
          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;

          // Draw the image on the canvas
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Convert canvas to Base64
          const base64Image = canvas.toDataURL(compressedFile.type);
          setImagePreview(base64Image);
          onImageChange(base64Image, compressedFile.type); // Only pass the base64 image string
          stopLoading();
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(compressedFile);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center", // ✅ Keeps both image and button centered
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      {/* Image Container */}
      <Box
        sx={{
          position: "relative",
          width: 200,
          height: 200,
          borderRadius: "8px",
          overflow: "hidden",
          border: `1px solid ${CONSTANTS.COLORS.IMAGE_BORDER_BLUE}`,
          boxShadow: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        {/* Conditionally render the image */}
        {imagePreview || photo ? (
          <>
            <img
              src={imagePreview || photo}
              alt="Uploaded"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            {/* Tick icon positioned at the top-right */}
            {/* <CheckCircleIcon
              sx={{
                position: 'absolute',
                top: 5, // Position the icon at the top
                right: 5, // Position the icon at the right
                color: 'green',
                backgroundColor: 'white',
                borderRadius: '50%',
                fontSize: '1.5rem',
              }}
            /> */}
            {/* Active label near the tick icon */}
            {/* <Typography
              sx={{
                position: 'absolute',
                top: 5,
                right: 40,  // Slightly move "Active" to the right of the tick icon
                fontSize: '1rem',
                color: 'green',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Active
            </Typography> */}
          </>
        ) : (
          <img
            src="/icon_photo_placeholder.svg"
            alt="Placeholder"
            style={{
              width: "125px",
              height: "125px",
              objectFit: "cover",
            }}
          />
        )}
      </Box>

      {/* File Upload Button */}
      {!previewOnly && (
        <StyledButtons.PrimaryButton
          component="label"
          sx={{
            width: "fit-content", // ✅ Button only as wide as text
            minWidth: "150px", // ✅ Optional, consistent size
            padding: "8px 16px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            whiteSpace: "nowrap",
            textAlign: "center",
            margin: "0 auto",
          }}
        >
          {t("adminDashboard.form.button.chooseFile")}
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            hidden
            onChange={handleImageUpload}
          />
        </StyledButtons.PrimaryButton>
      )}
    </Box>
  );
};

export default ImageUpload;
